/**
 * CoachingMotivation — Human Engagement & Motivation Panel
 * 
 * Motivates the human to keep training their cognitive twin through:
 * - 🔥 Streak counter with flame animation
 * - Time saved counter (ROI display)
 * - Correction trend (declining = twin is learning)
 * - Quick action CTAs
 * - Data ownership reinforcement
 * 
 * The primary motivation: "Your replica is already doing useful work,
 * and every minute you spend improving it pays you back 10x."
 */

import { useState, useEffect } from 'react';
import {
    Flame,
    Clock,
    TrendingDown,
    TrendingUp,
    CheckCircle,
    ArrowRight,
    Sparkles,
    Brain,
    Star,
    Zap,
    Calendar,
    Award,
} from 'lucide-react';
import { api } from '../../lib/api';
import HRLogo from '../HRLogo';

interface CoachingStreakData {
    currentStreak: number;
    longestStreak: number;
    lastSessionAt: string | null;
    totalSessions: number;
    totalMinutesSaved: number;
    weekMinutesSaved: number;
    thisWeekSessions: number;
    thisWeekCorrections: number;
    lastWeekCorrections: number;
    correctionTrend: number;
    correctionTrendLabel: string;
    isActive: boolean;
    trainedToday: boolean;
}

interface MotivationMessage {
    message: string;
}

interface CoachingMotivationProps {
    onNavigateToTraining?: () => void;
    compact?: boolean;
}

export default function CoachingMotivation({ onNavigateToTraining, compact = false }: CoachingMotivationProps) {
    const [streak, setStreak] = useState<CoachingStreakData | null>(null);
    const [motivation, setMotivation] = useState<MotivationMessage | null>(null);
    const [loading, setLoading] = useState(true);
    const [flameAnimating, setFlameAnimating] = useState(false);

    useEffect(() => {
        loadStreak();
    }, []);

    // Flame pulse animation
    useEffect(() => {
        if (streak && streak.currentStreak > 0) {
            const interval = setInterval(() => {
                setFlameAnimating(true);
                setTimeout(() => setFlameAnimating(false), 600);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [streak?.currentStreak]);

    async function loadStreak() {
        setLoading(true);
        try {
            const res = await api.get<any>('/cognitive/coaching/streak');
            if (res.data?.streak) setStreak(res.data.streak);
            if (res.data?.motivation) setMotivation(res.data.motivation);
        } catch (err) {
            console.error('Failed to load coaching streak:', err);
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingPulse}>
                    <Flame size={24} style={{ opacity: 0.3 }} />
                    <span style={{ opacity: 0.5, fontSize: 12 }}>Loading...</span>
                </div>
            </div>
        );
    }

    // Use default data when API hasn't returned anything yet
    const safeStreak: CoachingStreakData = streak || {
        currentStreak: 0,
        longestStreak: 0,
        lastSessionAt: null,
        totalSessions: 0,
        totalMinutesSaved: 0,
        weekMinutesSaved: 0,
        thisWeekSessions: 0,
        thisWeekCorrections: 0,
        lastWeekCorrections: 0,
        correctionTrend: 0,
        correctionTrendLabel: 'No data yet',
        isActive: false,
        trainedToday: false,
    };

    const streakColor = safeStreak.currentStreak >= 7
        ? '#f97316' // Orange for hot streaks
        : safeStreak.currentStreak >= 3
            ? '#fbbf24' // Amber
            : safeStreak.trainedToday
                ? '#34d399' // Green
                : 'rgba(255,255,255,0.3)'; // Inactive

    // Compact mode for dashboard
    if (compact) {
        return (
            <div style={styles.compactContainer}>
                <div style={styles.compactRow}>
                    {/* Streak */}
                    <div style={styles.compactBlock}>
                        <div style={{
                            ...styles.flameIcon,
                            transform: flameAnimating ? 'scale(1.3)' : 'scale(1)',
                        }}>
                            <Flame
                                size={22}
                                color={streakColor}
                                fill={safeStreak.currentStreak > 0 ? streakColor : 'none'}
                            />
                        </div>
                        <div style={styles.compactInfo}>
                            <span style={{ ...styles.compactValue, color: streakColor }}>
                                {safeStreak.currentStreak}
                            </span>
                            <span style={styles.compactLabel}>
                                day{safeStreak.currentStreak !== 1 ? 's' : ''} streak
                            </span>
                        </div>
                    </div>

                    {/* Time Saved */}
                    <div style={styles.compactBlock}>
                        <Clock size={18} color="#34d399" />
                        <div style={styles.compactInfo}>
                            <span style={{ ...styles.compactValue, color: '#34d399' }}>
                                {safeStreak.totalMinutesSaved || 0}
                            </span>
                            <span style={styles.compactLabel}>min saved</span>
                        </div>
                    </div>

                    {/* Sessions */}
                    <div style={styles.compactBlock}>
                        <Zap size={18} color="var(--accent)" />
                        <div style={styles.compactInfo}>
                            <span style={{ ...styles.compactValue, color: 'var(--accent)' }}>
                                {safeStreak.totalSessions}
                            </span>
                            <span style={styles.compactLabel}>sessions</span>
                        </div>
                    </div>
                </div>

                {/* CTA */}
                {!safeStreak.trainedToday && onNavigateToTraining && (
                    <button
                        onClick={onNavigateToTraining}
                        style={{ ...styles.compactCTA, background: 'var(--accent)', color: '#0F172A' }}
                    >
                        <Brain size={14} />
                        Train your twin today
                        <ArrowRight size={14} />
                    </button>
                )}

                {safeStreak.trainedToday && (
                    <div style={styles.trainedBadge}>
                        <CheckCircle size={14} color="#34d399" />
                        <span>Trained today • Your twin is learning</span>
                    </div>
                )}
            </div>
        );
    }

    // Full view
    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <div style={{
                        ...styles.flameIconLarge,
                        transform: flameAnimating ? 'scale(1.2)' : 'scale(1)',
                    }}>
                        <Flame
                            size={28}
                            color={streakColor}
                            fill={safeStreak.currentStreak > 0 ? streakColor : 'none'}
                        />
                    </div>
                    <div>
                        <h3 style={styles.title}>Your Coaching Impact</h3>
                        <p style={styles.subtitle}>
                            Every minute you invest makes your twin smarter
                        </p>
                    </div>
                </div>
            </div>

            {/* Stats Grid or Empty State (Fix 4) */}
            {(() => {
                const allZero = safeStreak.currentStreak === 0 &&
                    safeStreak.totalMinutesSaved === 0 &&
                    safeStreak.totalSessions === 0 &&
                    safeStreak.correctionTrend === 0;

                if (allZero) {
                    return (
                        <div style={{
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                            minHeight: 120, gap: 8, textAlign: 'center',
                            padding: 20,
                        }}>
                            <HRLogo size={32} state="default" showGlow={false} />
                            <div style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>
                                No training sessions yet
                            </div>
                            <div style={{
                                fontSize: 11, color: 'rgba(255,255,255,0.3)',
                                lineHeight: 1.5, maxWidth: 180,
                            }}>
                                Complete your first training session to start tracking your impact
                            </div>
                        </div>
                    );
                }

                return (
            <div style={styles.statsGrid}>
                {/* Streak */}
                <div style={styles.statCard}>
                    <div style={styles.statIcon}>
                        <Flame size={20} color={streakColor} fill={safeStreak.currentStreak > 0 ? streakColor : 'none'} />
                    </div>
                    <div style={styles.statInfo}>
                        <span style={{ ...styles.statValue, color: streakColor }}>
                            {safeStreak.currentStreak}
                        </span>
                        <span style={styles.statLabel}>Day Streak</span>
                    </div>
                    {safeStreak.longestStreak > 0 && (
                        <div style={styles.statBadge}>
                            <Star size={10} color="#fbbf24" />
                            <span>Best: {safeStreak.longestStreak}</span>
                        </div>
                    )}
                </div>

                {/* Time Saved */}
                <div style={styles.statCard}>
                    <div style={styles.statIcon}>
                        <Clock size={20} color="#34d399" />
                    </div>
                    <div style={styles.statInfo}>
                        <span style={{ ...styles.statValue, color: '#34d399' }}>
                            {safeStreak.totalMinutesSaved || 0}<span style={styles.statUnit}>min</span>
                        </span>
                        <span style={styles.statLabel}>Total Time Saved</span>
                    </div>
                    {safeStreak.weekMinutesSaved > 0 && (
                        <div style={{ ...styles.statBadge, backgroundColor: 'rgba(52,211,153,0.1)', color: '#34d399' }}>
                            <Zap size={10} />
                            <span>{safeStreak.weekMinutesSaved}min this week</span>
                        </div>
                    )}
                </div>

                {/* Sessions */}
                <div style={styles.statCard}>
                    <div style={styles.statIcon}>
                        <Calendar size={20} color="var(--accent)" />
                    </div>
                    <div style={styles.statInfo}>
                        <span style={{ ...styles.statValue, color: 'var(--accent)' }}>
                            {safeStreak.totalSessions}
                        </span>
                        <span style={styles.statLabel}>Total Sessions</span>
                    </div>
                    {safeStreak.thisWeekSessions > 0 && (
                        <div style={{ ...styles.statBadge, backgroundColor: 'rgba(0,229,255,0.1)', color: 'var(--accent)' }}>
                            <span>{safeStreak.thisWeekSessions} this week</span>
                        </div>
                    )}
                </div>

                {/* Correction Trend */}
                <div style={styles.statCard}>
                    <div style={styles.statIcon}>
                        {safeStreak.correctionTrend >= 0 ? (
                            <TrendingDown size={20} color="#34d399" />
                        ) : (
                            <TrendingUp size={20} color="#f97316" />
                        )}
                    </div>
                    <div style={styles.statInfo}>
                        <span style={{
                            ...styles.statValue,
                            color: safeStreak.correctionTrend >= 0 ? '#34d399' : '#f97316',
                        }}>
                            {safeStreak.correctionTrend > 0 ? '-' : ''}
                            {Math.abs(safeStreak.correctionTrend)}%
                        </span>
                        <span style={styles.statLabel}>Corrections Trend</span>
                    </div>
                    <div style={{
                        ...styles.statBadge,
                        backgroundColor: safeStreak.correctionTrend >= 0 ? 'rgba(52,211,153,0.1)' : 'rgba(249,115,22,0.1)',
                        color: safeStreak.correctionTrend >= 0 ? '#34d399' : '#f97316',
                    }}>
                        <span>{safeStreak.correctionTrendLabel}</span>
                    </div>
                </div>
            </div>
                );
            })()}

            {/* Motivation Message */}
            {motivation && (
                <div style={styles.motivationCard}>
                    <Sparkles size={16} color="#fbbf24" />
                    <span>{motivation.message}</span>
                </div>
            )}

            {/* CTA */}
            {!safeStreak.trainedToday && onNavigateToTraining && (
                <button
                    onClick={onNavigateToTraining}
                    style={{ ...styles.ctaButton, background: 'var(--accent)', color: '#0F172A', boxShadow: 'none' }}
                >
                    <Brain size={18} />
                    <span>Train Your Twin Now</span>
                    <ArrowRight size={16} />
                </button>
            )}

            {safeStreak.trainedToday && (
                <div style={styles.trainedTodayCard}>
                    <CheckCircle size={18} color="#34d399" />
                    <div>
                        <span style={styles.trainedTitle}>You trained today!</span>
                        <span style={styles.trainedSub}>
                            Every session teaches your twin something new about how you think and work.
                        </span>
                    </div>
                </div>
            )}

            {/* ROI Footer */}
            <div style={styles.footer}>
                <Award size={14} color="rgba(255,255,255,0.3)" />
                <span style={styles.footerText}>
                    Your data stays yours. Your twin learns only from YOU.
                </span>
            </div>
        </div>
    );
}

// ============================================
// STYLES
// ============================================

const styles: Record<string, React.CSSProperties> = {
    container: {
        background: '#111827',
        borderRadius: 12,
        border: '0.5px solid rgba(255,255,255,0.07)',
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    loadingPulse: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '30px 0',
        color: '#fbbf24',
        gap: 6,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    flameIconLarge: {
        transition: 'transform 0.3s ease',
    },
    title: {
        fontSize: 18,
        fontWeight: 700,
        color: '#fff',
        margin: 0,
    },
    subtitle: {
        fontSize: 13,
        color: 'rgba(255,255,255,0.5)',
        margin: 0,
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 10,
    },
    statCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '14px',
        borderRadius: 12,
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
    },
    statIcon: {
        display: 'flex',
    },
    statInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    statValue: {
        fontSize: 24,
        fontWeight: 800,
        lineHeight: 1,
    },
    statUnit: {
        fontSize: 13,
        fontWeight: 500,
        opacity: 0.7,
    },
    statLabel: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.4)',
        textTransform: 'uppercase' as const,
        letterSpacing: 0.5,
    },
    statBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        padding: '3px 8px',
        borderRadius: 6,
        backgroundColor: 'rgba(251,191,36,0.1)',
        color: '#fbbf24',
        width: 'fit-content',
    },
    motivationCard: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 16px',
        borderRadius: 10,
        background: 'rgba(251,191,36,0.05)',
        border: '1px solid rgba(251,191,36,0.12)',
        fontSize: 13,
        color: 'rgba(255,255,255,0.8)',
        lineHeight: 1.4,
    },
    ctaButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: '14px 24px',
        borderRadius: 12,
        border: 'none',
        background: 'linear-gradient(135deg, #a78bfa, #8b5cf6)',
        color: '#fff',
        fontSize: 15,
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 4px 20px rgba(167,139,250,0.3)',
    },
    trainedTodayCard: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 16px',
        borderRadius: 10,
        background: 'rgba(52,211,153,0.05)',
        border: '1px solid rgba(52,211,153,0.15)',
    },
    trainedTitle: {
        display: 'block',
        fontSize: 14,
        fontWeight: 600,
        color: '#34d399',
    },
    trainedSub: {
        display: 'block',
        fontSize: 12,
        color: 'rgba(255,255,255,0.5)',
        marginTop: 2,
        lineHeight: 1.4,
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingTop: 10,
        borderTop: '1px solid rgba(255,255,255,0.04)',
    },
    footerText: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.3)',
        fontStyle: 'italic',
    },
    // Compact styles
    compactContainer: {
        background: '#111827',
        borderRadius: 12,
        border: '0.5px solid rgba(255,255,255,0.07)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    compactRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    compactBlock: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    flameIcon: {
        transition: 'transform 0.3s ease',
        display: 'flex',
    },
    compactInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
    },
    compactValue: {
        fontSize: 18,
        fontWeight: 800,
        lineHeight: 1,
    },
    compactLabel: {
        fontSize: 9,
        color: 'rgba(255,255,255,0.4)',
        textTransform: 'uppercase' as const,
        letterSpacing: 0.5,
    },
    compactCTA: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '10px 16px',
        borderRadius: 10,
        border: 'none',
        background: 'linear-gradient(135deg, #a78bfa, #8b5cf6)',
        color: '#fff',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    trainedBadge: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        fontSize: 11,
        color: 'rgba(255,255,255,0.5)',
        padding: '6px 12px',
        borderRadius: 8,
        background: 'rgba(52,211,153,0.05)',
    },
};
